import { useQuery } from "@tanstack/react-query";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import type {
  DeductibleResolutionEvent,
  DeductibleReview,
  ResolveDeductibleReviewRequest,
} from "@/types/deductibleReview";

/**
 * Deductible reviews and resolution history for a claimant on a policy
 * (Feature #3502, #7340/#3506). Both lists are `{ data, pagination }` envelopes,
 * newest first. Query keys are refreshed by useRecomputeRefresh.
 */
const claimantUrl = (claimantId: string) => `${getPartnerAwareApiUrl()}/claimants/${claimantId}`;

export const useDeductibleReviews = (claimantId: string | undefined, policyId: string | undefined, enabled = true) => {
  const { data, isLoading, isFetching, isFetchedAfterMount, refetch, error } = useQuery({
    queryKey: ["deductible-reviews", claimantId, policyId],
    queryFn: async (): Promise<DeductibleReview[]> => {
      const { json } = await httpClient(
        `${claimantUrl(claimantId as string)}/deductible-reviews?policy_id=${encodeURIComponent(policyId as string)}`
      );
      return ((json as { data?: DeductibleReview[] })?.data ?? []) as DeductibleReview[];
    },
    enabled: enabled && !!claimantId && !!policyId,
    staleTime: 0,
  });
  const reviews = data ?? [];
  return {
    reviews,
    openReview: reviews.find((r) => r.status === "open"),
    isLoading,
    /** True while any fetch runs, including a background refetch of cached data. */
    isFetching,
    /** True once a fetch for the current key has settled since this caller mounted. */
    isFetchedAfterMount,
    error,
    refetch,
  };
};

export const useDeductibleResolutionEvents = (
  claimantId: string | undefined,
  policyId: string | undefined,
  enabled = true
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["deductible-resolution-events", claimantId, policyId],
    queryFn: async (): Promise<DeductibleResolutionEvent[]> => {
      const { json } = await httpClient(
        `${claimantUrl(claimantId as string)}/deductible-resolution-events?policy_id=${encodeURIComponent(
          policyId as string
        )}`
      );
      return ((json as { data?: DeductibleResolutionEvent[] })?.data ?? []) as DeductibleResolutionEvent[];
    },
    enabled: enabled && !!claimantId && !!policyId,
  });
  return { events: data ?? [], isLoading, refetch };
};

/** POST .../resolve. A 409 means the review was superseded or the candidate is not its own. */
export const resolveDeductibleReview = async (
  claimantId: string,
  reviewId: string,
  body: ResolveDeductibleReviewRequest
): Promise<DeductibleReview> => {
  const { json } = await httpClient(`${claimantUrl(claimantId)}/deductible-reviews/${reviewId}/resolve`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return json as DeductibleReview;
};
